import { DataService } from "../../shared/services/data.service";
import { CacheService } from "../../shared/services/cache.service";

class AuthenticateServiceImpl {
  static instance = null;

  static getInstance() {
    if (!AuthenticateServiceImpl.instance) {
      AuthenticateServiceImpl.instance = new AuthenticateServiceImpl();
    }
    return AuthenticateServiceImpl.instance;
  }

  async authenticate(username, password, rememberMe) {
    const response = await DataService.post(
      "token",
      { username, password },
      {},
    );
    if (!response.token) {
      throw new Error("Unable to find token");
    }
    CacheService.putIntoSession("security", "token", response.token);
    if (rememberMe) {
      CacheService.put("security", "token", response.token);
    }

    return this.refreshUser();
  }

  getActiveUser() {
    return CacheService.getFromSession("security", "user");
  }

  getAccessibleBank() {
    const user = this.getActiveUser();
    return user &&
      user.accessibleTenantIds &&
      user.accessibleTenantIds.length > 0
      ? user.accessibleTenantIds[0]
      : -1;
  }

  async refreshUser() {
    const user = await DataService.get("users/me");
    let status = user && user.status ? user.status.toLowerCase() : "active";
    if (!user || ["expired", "locked", "inactive"].indexOf(status) > -1) {
      this.clear();
      return null;
    }
    CacheService.putIntoSession("security", "user", user);
    return user;
  }

  getFunctionsOfType(type) {
    const user = this.getActiveUser();
    const functions = [];
    if (
      user &&
      user.profile &&
      Array.isArray(user.profile.accessibleFunctions) &&
      user.profile.accessibleFunctions.length > 0
    ) {
      user.profile.accessibleFunctions.forEach((fnc) => {
        if (fnc.access === "A" && fnc.type === type) {
          functions.push(fnc);
        }
      });
    }
    return functions;
  }

  getHeaderLinks() {
    return this.getFunctionsOfType("HL");
  }

  getSidebarLinks() {
    return this.getFunctionsOfType("SBL");
  }

  async getProfiles() {
    let profiles = CacheService.getFromSession("users", "profiles");
    if (profiles === null) {
      profiles = await DataService.get("profiles", {
        overrideSelectedTenant: true,
      });
      CacheService.putIntoSession("users", "profiles", profiles);
    }
    return profiles;
  }

  async getTenants() {
    let tenants = CacheService.getFromSession("users", "tenants");
    if (tenants === null) {
      tenants = await DataService.get("tenants", {});
      CacheService.putIntoSession("users", "tenants", tenants);
    }
    return tenants;
  }

  getMenuTree() {
    const user = this.getActiveUser();
    const functions = (user.profile || {}).accessibleFunctions || [];
    const menus = [];
    const menuMap = {};
    functions.forEach((fnc) => {
      if (
        !fnc ||
        ["M", "SM"].indexOf(fnc.type) === -1 ||
        ["A", "R", "W"].indexOf(fnc.access) === -1 ||
        !fnc.functionId ||
        !fnc.caption
      ) {
        return;
      }
      const menu = {
        id: fnc.functionId,
        parentId: fnc.parentFunctionId,
        icon: fnc.icon,
        caption: fnc.caption,
        link: fnc.link,
        children: [],
      };
      menuMap[menu.id] = menu;
      menus.push(menu);
    });

    const rootMenus = [];

    menus.forEach((menu) => {
      const parentMenu = menuMap[menu.parentId];
      if (parentMenu) {
        parentMenu.children.push(menu);
      } else {
        rootMenus.push(menu);
      }
    });

    return rootMenus;
  }

  clear() {
    CacheService.clear();
  }
}

export const AuthenticateService = AuthenticateServiceImpl.getInstance();
